import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { useUpdateSignupFlowMutation } from '@/modules/signup-flow/mutations/useUpdateSignupFlowMutation';
import { signupFlowStep } from '@/modules/signup-flow/signupFlowSteps';
import { useSlackNotificationChannelQuery } from '@/modules/slack-notification-channel/queries/useSlackNotificationChannelQuery';
import { useIsSlackConnected } from '@/modules/user-external-accounts/hooks/useIsSlackConnected';
import { useUserExternalAccountsQuery } from '@/modules/user-external-accounts/queries/useUserExternalAccountsQuery';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import {
  type SummaryIntrosDestination,
  SummaryNextSteps,
} from './SummaryNextSteps';

export const Summary = () => {
  const trackEvent = useTrackEvent();
  const navigate = useNavigate();

  const currentUserQuery = useCurrentUserQuery();
  const { userId, isSlackConnected } = useIsSlackConnected();
  // Same cache entry `useIsSlackConnected` reads; needed here for its pending/error state.
  const externalAccountsQuery = useUserExternalAccountsQuery(userId);
  const slackChannelQuery = useSlackNotificationChannelQuery({
    enabled: isSlackConnected,
  });
  const updateSignupFlowMutation = useUpdateSignupFlowMutation();

  const slackAccount = externalAccountsQuery.data?.find(
    (account) => account.type === 'Slack' && account.status === 'Active',
  );
  const slackChannel = slackChannelQuery.data;

  const isLoading =
    currentUserQuery.isPending ||
    externalAccountsQuery.isPending ||
    (isSlackConnected && slackChannelQuery.isPending);

  const introsDestination: SummaryIntrosDestination =
    externalAccountsQuery.isError || slackChannelQuery.isError
      ? { kind: 'unknown' }
      : !isSlackConnected
        ? { kind: 'finlay' }
        : slackChannel?.channelName
          ? {
              kind: 'slackChannel',
              channelName: slackChannel.channelName,
              slackTarget: {
                teamId: slackChannel.teamId,
                channelId: slackChannel.channelId,
              },
            }
          : {
              kind: 'slackDirectMessages',
              slackTarget: slackAccount?.appId
                ? { appId: slackAccount.appId, teamId: slackAccount.teamId }
                : undefined,
            };

  const handleContinue = ({ openedSlack }: { openedSlack: boolean }) => {
    updateSignupFlowMutation.mutate(
      { step: signupFlowStep.summary, isComplete: true },
      {
        onError: () => {
          toast.error('Failed to complete setup. Please try again.');
        },
        onSuccess: () => {
          trackEvent('summaryFinished', { openedSlack });
          navigate('/hm');
        },
      },
    );
  };

  return (
    <SummaryNextSteps
      firstName={currentUserQuery.data?.firstName ?? undefined}
      isLoading={isLoading}
      introsDestination={introsDestination}
      isSubmitting={updateSignupFlowMutation.isPending}
      onContinue={handleContinue}
    />
  );
};
